import { FC, useState } from "react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import {
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Input,
  Button,
  Checkbox,
  Badge,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@capacit-com/lara-law-shared-ui";
import {
  Trash2,
  Plus,
  Pencil,
  Check,
  X,
  AlertCircle,
  MoreVertical,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import {
  StaticListDataSource,
  StaticListWidgetConfig,
  StaticListItem,
} from "@/models/dashboard/WidgetConfig";
import {
  buildLegislationOriginalId,
  getStaticListColumns,
  migrateStaticListItems,
  partitionStaticListItems,
  resolveStaticListItemType,
  sortStaticListItems,
} from "../lib/staticListItems";
import {
  isWidgetColumnVisible,
  toggleWidgetColumn,
} from "../lib/widgetColumns";
import { uuidv4 } from "@capacit-com/lara-law-shared-ui/uuid";
import WorkspaceDocumentPickerDialog, {
  SelectedWorkspaceDocument,
} from "./WorkspaceDocumentPickerDialog";
import LegislationPickerDialog, {
  SelectedLegislation,
} from "./LegislationPickerDialog";
import { useStaticListItemResolver } from "@/hooks/useStaticListItemResolver";
import WidgetItemRow from "./WidgetItemRow";

interface StaticListConfigFormProps {
  config: StaticListWidgetConfig;
  onChange: (config: StaticListWidgetConfig) => void;
}

const StaticListConfigForm: FC<StaticListConfigFormProps> = ({
  config,
  onChange,
}) => {
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [workspacePickerOpen, setWorkspacePickerOpen] = useState(false);
  const [legislationPickerOpen, setLegislationPickerOpen] = useState(false);

  // Get resolved item info for preview
  const { getResolvedInfo } = useStaticListItemResolver(
    config.items,
    config.dataSource
  );

  const columns = getStaticListColumns(config.dataSource);

  // A mixed list offers both pickers; the single-kind lists offer only theirs.
  const canAddSources = config.dataSource !== "workspace-documents";
  const canAddWorkspaceDocuments = config.dataSource !== "sources";

  const updateConfig = (updates: Partial<StaticListWidgetConfig>) => {
    onChange({ ...config, ...updates });
  };

  // Each picker only excludes the ids it can actually match — a legislation
  // "externalId|datasource" means nothing to the workspace picker, and vice versa.
  const { sourceItems, workspaceDocumentItems } = partitionStaticListItems(
    config.items,
    config.dataSource
  );
  const existingSourceIds = sourceItems.map((item) => item.originalId);
  const existingWorkspaceDocumentIds = workspaceDocumentItems.map(
    (item) => item.originalId
  );

  // Handle workspace document selection
  const handleWorkspaceDocumentsSelected = (
    documents: SelectedWorkspaceDocument[]
  ) => {
    const newItems: StaticListItem[] = documents.map((doc, index) => ({
      id: uuidv4(),
      originalId: doc.id,
      customName: doc.name,
      sortOrder: config.items.length + index,
      itemType: "workspace-document",
      workspaceId: doc.workspaceId,
      workspaceName: doc.workspaceName,
    }));

    updateConfig({ items: [...config.items, ...newItems] });
  };

  // Handle legislation selection
  const handleLegislationSelected = (documents: SelectedLegislation[]) => {
    const newItems: StaticListItem[] = documents.map((doc, index) => ({
      id: uuidv4(),
      originalId: buildLegislationOriginalId(doc.externalId, doc.datasource),
      customName: doc.title,
      sortOrder: config.items.length + index,
      itemType: "source",
    }));

    updateConfig({ items: [...config.items, ...newItems] });
  };

  const removeItem = (itemId: string) => {
    const newItems = config.items
      .filter((item) => item.id !== itemId)
      .map((item, index) => ({ ...item, sortOrder: index }));
    updateConfig({ items: newItems });
  };

  const startEditing = (item: StaticListItem) => {
    setEditingItemId(item.id);
    const resolved = getResolvedInfo(item.id);
    setEditingName(item.customName || resolved.title || item.originalId);
  };

  const saveEditing = () => {
    if (!editingItemId) return;

    const newItems = config.items.map((item) =>
      item.id === editingItemId
        ? { ...item, customName: editingName || undefined }
        : item
    );
    updateConfig({ items: newItems });
    setEditingItemId(null);
    setEditingName("");
  };

  const cancelEditing = () => {
    setEditingItemId(null);
    setEditingName("");
  };

  const moveItem = (itemId: string, direction: "up" | "down") => {
    const index = config.items.findIndex((item) => item.id === itemId);
    if (index === -1) return;

    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= config.items.length) return;

    const newItems = [...config.items];
    [newItems[index], newItems[newIndex]] = [
      newItems[newIndex],
      newItems[index],
    ];

    const reorderedItems = newItems.map((item, idx) => ({
      ...item,
      sortOrder: idx,
    }));

    updateConfig({ items: reorderedItems });
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;
    if (sourceIndex === destinationIndex) return;

    const sortedItems = sortStaticListItems(config.items);
    const [removed] = sortedItems.splice(sourceIndex, 1);
    sortedItems.splice(destinationIndex, 0, removed);

    const reorderedItems = sortedItems.map((item, idx) => ({
      ...item,
      sortOrder: idx,
    }));

    updateConfig({ items: reorderedItems });
  };

  const handleColumnToggle = (columnId: string, checked: boolean) => {
    updateConfig({
      visibleColumns: toggleWidgetColumn(
        columns,
        config.visibleColumns,
        columnId,
        checked
      ),
    });
  };

  // An empty selection means "the defaults", so unticking the last column
  // would silently bring them all back — keep one column ticked instead.
  const visibleColumnCount = columns.filter((column) =>
    isWidgetColumnVisible(column, config.visibleColumns)
  ).length;

  return (
    <div className="space-y-6">
      {/* Data Source */}
      <div className="space-y-2">
        <Label>Datakilde</Label>
        <Select
          value={config.dataSource}
          onValueChange={(value: StaticListDataSource) =>
            updateConfig({
              dataSource: value,
              visibleColumns: [],
              items: migrateStaticListItems(
                config.items,
                config.dataSource,
                value
              ),
            })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="sources">Kilder (Lovgivning)</SelectItem>
            <SelectItem value="workspace-documents">
              Workspace dokumenter
            </SelectItem>
            <SelectItem value="mixed">
              Blandet (kilder og workspace dokumenter)
            </SelectItem>
          </SelectContent>
        </Select>
        {config.dataSource === "mixed" && (
          <p className="text-sm text-muted-foreground">
            Listen kan indeholde både lovgivning og workspace dokumenter.
          </p>
        )}
      </div>

      {/* Items List with Preview */}
      <div className="space-y-2">
        <Label>Dokumenter</Label>
        <p className="text-sm text-muted-foreground">
          Tilføj dokumenter til listen. Du kan omdøbe dem og ændre rækkefølgen.
        </p>

        {config.items.length > 0 && (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="static-list-items">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="border rounded-lg divide-y overflow-hidden w-full"
                >
                  {sortStaticListItems(config.items).map((item, index) => {
                    const resolved = getResolvedInfo(item.id);
                    const itemType = resolveStaticListItemType(
                      item,
                      config.dataSource
                    );
                    const displayTitle = item.customName || resolved.title;
                    const isEditing = editingItemId === item.id;

                    return (
                      <Draggable
                        key={item.id}
                        draggableId={item.id}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <WidgetItemRow
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            dragHandleProps={provided.dragHandleProps}
                            isDragging={snapshot.isDragging}
                            kindBadge={
                              config.dataSource === "mixed" && (
                                <Badge variant="outline" className="text-xs">
                                  {itemType === "source"
                                    ? "Kilder"
                                    : "Workspace"}
                                </Badge>
                              )
                            }
                            title={
                              isEditing ? (
                                <>
                                  <Input
                                    value={editingName}
                                    onChange={(e) =>
                                      setEditingName(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") saveEditing();
                                      if (e.key === "Escape") cancelEditing();
                                    }}
                                    // Esc cancels the rename, not the whole editor panel.
                                    data-escape-local
                                    className="h-8 flex-1 min-w-0"
                                    placeholder="Brugerdefineret navn"
                                    aria-label="Nyt navn"
                                    autoFocus
                                  />
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 shrink-0"
                                    onClick={saveEditing}
                                    aria-label="Gem navn"
                                  >
                                    <Check className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 shrink-0"
                                    onClick={cancelEditing}
                                    aria-label="Annuller omdøbning"
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </>
                              ) : resolved.isLoading ? (
                                <Skeleton className="h-5 w-48 shrink-0" />
                              ) : resolved.isError && !item.customName ? (
                                // Show the destructive state when enrichment failed
                                // and the user hasn't provided a customName fallback.
                                // (When customName exists, we prefer to show it as the
                                // displayTitle and surface the failure via missing badges.)
                                <div className="flex items-center gap-2 text-destructive min-w-0">
                                  <AlertCircle className="h-4 w-4 shrink-0" />
                                  <span className="text-sm">
                                    Kunne ikke indlæse dokument
                                  </span>
                                </div>
                              ) : (
                                // Prefer the stored customName / resolved title even when
                                // enrichment failed — matches widget behaviour.
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="text-sm font-medium truncate min-w-0">
                                      {displayTitle}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="max-w-[300px] break-words">
                                      {displayTitle}
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              )
                            }
                            sourceLine={
                              !isEditing && (
                                <>
                                  {/* Metadata — only shown when enrichment succeeded */}
                                  {!resolved.isLoading && !resolved.isError && (
                                    <>
                                      {resolved.type &&
                                        resolved.type !== "-" && (
                                          <Badge
                                            variant="secondary"
                                            className="text-xs shrink-0"
                                          >
                                            {resolved.type}
                                          </Badge>
                                        )}
                                      {resolved.status &&
                                        resolved.status !== "-" && (
                                          <Badge
                                            variant={
                                              resolved.status === "Gældende"
                                                ? "default"
                                                : "outline"
                                            }
                                            className="text-xs shrink-0"
                                          >
                                            {resolved.status}
                                          </Badge>
                                        )}
                                      {item.customName &&
                                        resolved.title &&
                                        item.customName !== resolved.title && (
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <span className="truncate min-w-0">
                                                (Original: {resolved.title})
                                              </span>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <p className="max-w-[300px] break-words">
                                                {resolved.title}
                                              </p>
                                            </TooltipContent>
                                          </Tooltip>
                                        )}
                                    </>
                                  )}
                                  {/* ID for debugging — always visible */}
                                  <span className="basis-full font-mono truncate min-w-0">
                                    {item.originalId}
                                  </span>
                                </>
                              )
                            }
                            menu={
                              !isEditing && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      aria-label={`Flere handlinger for ${displayTitle || "elementet"}`}
                                    >
                                      <MoreVertical className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem
                                      onSelect={() => startEditing(item)}
                                    >
                                      <Pencil className="h-4 w-4 mr-2" />
                                      Omdøb
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onSelect={() => moveItem(item.id, "up")}
                                      disabled={index === 0}
                                    >
                                      <ArrowUp className="h-4 w-4 mr-2" />
                                      Flyt op
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onSelect={() => moveItem(item.id, "down")}
                                      disabled={
                                        index === config.items.length - 1
                                      }
                                    >
                                      <ArrowDown className="h-4 w-4 mr-2" />
                                      Flyt ned
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onSelect={() => removeItem(item.id)}
                                      className="text-destructive focus:text-destructive"
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Fjern
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )
                            }
                          />
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}

        {/* Add Documents Buttons */}
        <div className="pt-2 flex flex-col gap-2 sm:flex-row">
          {canAddSources && (
            <Button
              variant="outline"
              onClick={() => setLegislationPickerOpen(true)}
              className="flex-1"
            >
              <Plus className="h-4 w-4 mr-2" />
              Vælg fra kilder
            </Button>
          )}
          {canAddWorkspaceDocuments && (
            <Button
              variant="outline"
              onClick={() => setWorkspacePickerOpen(true)}
              className="flex-1"
            >
              <Plus className="h-4 w-4 mr-2" />
              Vælg fra workspaces
            </Button>
          )}
        </div>

        {config.items.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-4">
            Ingen dokumenter tilføjet endnu.
          </p>
        )}
      </div>

      {/* Visible Columns */}
      <div className="space-y-2">
        <Label>Synlige kolonner</Label>
        <div className="grid grid-cols-2 gap-2 pt-2">
          {columns.map((column) => {
            const isChecked = isWidgetColumnVisible(
              column,
              config.visibleColumns
            );

            return (
              <div key={column.id} className="flex items-center space-x-2">
                <Checkbox
                  id={`static-col-${column.id}`}
                  checked={isChecked}
                  disabled={isChecked && visibleColumnCount === 1}
                  onCheckedChange={(checked) =>
                    handleColumnToggle(column.id, checked as boolean)
                  }
                />
                <Label
                  htmlFor={`static-col-${column.id}`}
                  className="font-normal"
                >
                  {column.label}
                </Label>
              </div>
            );
          })}
        </div>
      </div>

      {/* Picker Dialogs */}
      <WorkspaceDocumentPickerDialog
        open={workspacePickerOpen}
        onOpenChange={setWorkspacePickerOpen}
        onSelect={handleWorkspaceDocumentsSelected}
        excludeIds={existingWorkspaceDocumentIds}
      />

      <LegislationPickerDialog
        open={legislationPickerOpen}
        onOpenChange={setLegislationPickerOpen}
        onSelect={handleLegislationSelected}
        excludeIds={existingSourceIds}
      />
    </div>
  );
};

export default StaticListConfigForm;
